//* Libraries imports
import { motion } from 'framer-motion';
import { Container, Row, Col } from 'react-bootstrap';

//? Styles imports
import './result-view-skeleton.scss';

interface ResultViewSkeletonProps {
  showCertificate?: boolean;
}

/**
 * ResultViewSkeleton Component
 * 
 * Provides a skeleton loading state for the ResultView component.
 * Mimics the layout structure of the actual result view with animated placeholders.
 * 
 * @param showCertificate - Whether to show the certificate section skeleton
 */
export function ResultViewSkeleton({ showCertificate = true }: ResultViewSkeletonProps) {
  const skeletonVariants = {
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        repeat: Infinity
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <motion.div
      className="result-view-skeleton min-vh-100 bg-light"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Container className="p-4">

        {/* Student Info Skeleton */}
        <motion.div 
          className="skeleton-result-card mb-4"
          variants={itemVariants}
        >
          {/* Header Section */}
          <div className="skeleton-card-header">
            <div className="skeleton-header-content">
              {/* Back Button */}
              <motion.div
                className="skeleton-back-button"
                variants={skeletonVariants}
                animate="pulse"
              />
              
              {/* Title Section */}
              <div className="skeleton-title-section">
                <motion.div
                  className="skeleton-icon-wrapper"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <div className="skeleton-title-content">
                  <motion.div
                    className="skeleton-main-title"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                  <motion.div
                    className="skeleton-subtitle"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div className="skeleton-card-body">
            {/* Certificate Section */}
            {showCertificate && (
              <motion.div 
                className="skeleton-certificate-section mb-4"
                variants={itemVariants}
              >
                <div className="skeleton-certificate-banner">
                  <motion.div
                    className="skeleton-certificate-icon"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                  <div className="skeleton-certificate-content">
                    <motion.div
                      className="skeleton-certificate-title"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-certificate-subtitle"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                  <div className="skeleton-certificate-actions">
                    <motion.div
                      className="skeleton-action-button"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-action-button"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Student Information */}
            <motion.div 
              className="skeleton-student-section mb-4"
              variants={itemVariants}
            >
              <div className="skeleton-student-header">
                <motion.div
                  className="skeleton-avatar"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <div className="skeleton-student-info">
                  <motion.div
                    className="skeleton-student-name"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                  <motion.div
                    className="skeleton-student-subtitle"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                </div>
              </div>

              <Row className="mt-3">
                <Col md={6}>
                  <div className="skeleton-detail-item">
                    <motion.div
                      className="skeleton-detail-icon"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <div className="skeleton-detail-content">
                      <motion.div
                        className="skeleton-detail-label"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                      <motion.div
                        className="skeleton-detail-value"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="skeleton-detail-item">
                    <motion.div
                      className="skeleton-detail-icon"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <div className="skeleton-detail-content">
                      <motion.div
                        className="skeleton-detail-label"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                      <motion.div
                        className="skeleton-detail-value"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </motion.div>

            {/* Performance Statistics */}
            <motion.div 
              className="skeleton-performance-section"
              variants={itemVariants}
            >
              <div className="skeleton-section-header">
                <motion.div
                  className="skeleton-section-icon"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-section-title"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-exam-type-badge"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>

              <Row>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Col md={4} key={index}>
                    <div className="skeleton-stat-card">
                      <div className="skeleton-stat-header">
                        <motion.div
                          className="skeleton-stat-label"
                          variants={skeletonVariants}
                          animate="pulse"
                        />
                        <motion.div
                          className="skeleton-stat-icon"
                          variants={skeletonVariants}
                          animate="pulse"
                        />
                      </div>
                      <motion.div
                        className="skeleton-stat-value"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                      <motion.div
                        className="skeleton-stat-subtitle"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                      
                      {/* Progress bar for the first card (percentage) */}
                      {index === 0 && (
                        <div className="skeleton-progress-bar">
                          <motion.div
                            className="skeleton-progress-fill"
                            variants={skeletonVariants}
                            animate="pulse"
                          />
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </motion.div>
          </div>
        </motion.div>

        {/* Questions Section Skeleton */}
        <motion.div 
          className="skeleton-questions-section"
          variants={itemVariants}
        >
          {Array.from({ length: 5 }).map((_, questionIndex) => (
            <motion.div
              key={questionIndex}
              className="skeleton-question-card mb-4"
              variants={itemVariants}
              style={{
                animationDelay: `${questionIndex * 0.1}s`
              }}
            >
              {/* Question Header */}
              <div className="skeleton-question-header">
                <motion.div
                  className="skeleton-question-number"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-question-title"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>

              {/* Question Content */}
              <div className="skeleton-question-content">
                <motion.div
                  className="skeleton-question-text"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-question-text-short"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>

              {/* Answer Options */}
              <div className="skeleton-answer-options">
                {Array.from({ length: 4 }).map((_, optionIndex) => (
                  <motion.div
                    key={optionIndex}
                    className="skeleton-answer-option"
                    variants={skeletonVariants}
                    animate="pulse"
                    style={{
                      animationDelay: `${(questionIndex * 0.1) + (optionIndex * 0.05)}s`
                    }}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </motion.div>
  );
}
